import { createContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { FiCalendar } from 'react-icons/fi';
import CalendarPanel from './CalendarPanel';
import { asDate, dateKey, displayDate, inBounds } from './dates';
import './portalDatePicker.css';

export const PortalDateContext = createContext(false);
let nextId = 0;

// Uses the existing portal Calendar event shape so filters keep their date contracts.
export default function PortalDatePicker({ value, onChange, onClearButtonClick, inputId, id, name, label, placeholder, className = '', inputClassName = '', style, inputStyle, minDate, maxDate, disabled, required, onBlur, onFocus, 'aria-invalid': invalid, 'aria-describedby': describedBy }) {
    const [generatedId] = useState(() => `portal-date-${++nextId}`);
    const fieldId = inputId || id || generatedId;
    const panelId = `${generatedId}-calendar`;
    const selected = asDate(value), min = asDate(minDate), max = asDate(maxDate);
    const input = useRef(null), anchor = useRef(null);
    const [open, setOpen] = useState(false);
    const [position, setPosition] = useState({ visibility: 'hidden' });
    const fieldLabel = label || placeholder?.replace(/\s*\([^)]*\)/g, '').trim() || 'Date';
    const close = (restoreFocus = true) => { setOpen(false); if (restoreFocus) input.current?.focus({ preventScroll: true }); };
    const change = (date, originalEvent) => {
        onChange?.({ value: date, originalEvent, target: { name, id: fieldId, value: date } });
    };
    const select = (date) => { change(date); close(); };
    const clear = () => { if (onClearButtonClick) onClearButtonClick(); else change(null); close(); };
    const blur = (event) => {
        if (anchor.current?.contains(event.relatedTarget) || document.getElementById(panelId)?.contains(event.relatedTarget)) return;
        onBlur?.(event);
    };

    useLayoutEffect(() => {
        if (!open) return;
        const place = () => {
            const bounds = anchor.current.getBoundingClientRect();
            const width = Math.min(320, window.innerWidth - 24);
            const height = document.getElementById(panelId)?.getBoundingClientRect().height || 400;
            const left = Math.max(12, Math.min(bounds.left, window.innerWidth - width - 12));
            const below = bounds.bottom + 8;
            const top = below >= 12 && below + height <= window.innerHeight - 12 ? below : Math.max(12, Math.min(bounds.top - height - 8, window.innerHeight - height - 12));
            setPosition({ width, left, top, maxHeight: 'calc(100vh - 24px)' });
        };
        place();
        const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(place);
        const panel = document.getElementById(panelId);
        if (panel) observer?.observe(panel);
        window.addEventListener('resize', place);
        window.addEventListener('scroll', place, true);
        return () => { observer?.disconnect(); window.removeEventListener('resize', place); window.removeEventListener('scroll', place, true); };
    }, [open, panelId]);
    useEffect(() => {
        if (!open) return;
        const outside = (event) => {
            if (!anchor.current?.contains(event.target) && !document.getElementById(panelId)?.contains(event.target)) setOpen(false);
        };
        document.addEventListener('pointerdown', outside);
        return () => document.removeEventListener('pointerdown', outside);
    }, [open, panelId]);
    useEffect(() => { if (disabled) setOpen(false); }, [disabled]);

    return <span ref={anchor} className={`portal-date-picker${selected ? ' has-value' : ''}${open ? ' is-open' : ''} ${className}`} style={style}>
        <input ref={input} id={fieldId} name={name} type="text" role="combobox" aria-label={label || placeholder ? fieldLabel : undefined} aria-haspopup="dialog" aria-expanded={open} aria-controls={open ? panelId : undefined} aria-invalid={invalid} aria-describedby={describedBy} autoComplete="off" readOnly disabled={disabled} required={required} value={displayDate(selected)} placeholder="Choose date" className={`portal-date-input ${inputClassName}`} style={inputStyle} onClick={() => setOpen(true)} onFocus={onFocus} onBlur={blur} onChange={(event) => { const date = asDate(event.target.value); if (!event.target.value || (date && inBounds(date, min, max))) change(date, event); }} onKeyDown={(event) => { if (['Enter', ' ', 'ArrowDown'].includes(event.key)) { event.preventDefault(); setOpen(true); } }} />
        <button type="button" className="portal-date-trigger" tabIndex={-1} disabled={disabled} aria-label={`Open calendar for ${fieldLabel.toLowerCase()}`} onClick={() => setOpen((current) => !current)}><FiCalendar aria-hidden="true" /></button>
        {open && createPortal(<CalendarPanel id={panelId} label={fieldLabel} value={selected} minDate={min} maxDate={max} position={position} anchor={anchor} onSelect={select} onClear={clear} onClose={close} />, document.body)}
    </span>;
}

// Adapter for forms and APIs that store local calendar dates as YYYY-MM-DD strings.
export function PortalDateInput({ value, onChange, min, max, id, name, ...props }) {
    return <PortalDatePicker {...props} inputId={id} name={name} value={asDate(value)} minDate={asDate(min)} maxDate={asDate(max)} onChange={(event) => onChange?.({ ...event, target: { id, name, value: dateKey(event.value) } })} />;
}
